import { computed, onUnmounted } from 'vue'
import { useImportacaoStore } from '@/stores/importacao'
import importacaoService from '@/services/importacaoService'
import type {
  TipoCadastro,
  ProcessamentoResult,
  JobStatusResponse,
  JobStatusBackend,
  ItemFalha,
  ItemCorrecao,
  ImportacaoStatus,
  ErroInsercao
} from '@/types'
import { AxiosError } from 'axios'

const POLL_INTERVAL = 2000
const STATUS_FALHA = 'INVALIDO,DUPLICADO,ERRO'
const LIMITE_ITENS_FALHA = 500

export function useImportacao() {
  const store = useImportacaoStore()

  // Estados computados do store
  const tipoCadastro = computed(() => store.tipoCadastro)
  const status = computed(() => store.status)
  const arquivo = computed(() => store.upload.arquivo)
  const validacaoLocal = computed(() => store.validacaoLocal)
  const validacaoApi = computed(() => store.validacaoApi)
  const processamento = computed(() => store.processamento)
  const erro = computed(() => store.erro)
  const processados = computed(() => store.processados)
  const totalParaProcessar = computed(() => store.totalParaProcessar)
  const jobId = computed(() => store.jobId)
  const itensFalha = computed(() => store.itensFalha)

  // ---- Controle do polling ----
  let pollHandle: ReturnType<typeof setInterval> | null = null
  let consultando = false

  const pararPolling = () => {
    if (pollHandle) {
      clearInterval(pollHandle)
      pollHandle = null
    }
  }

  const extrairMensagemErro = (err: unknown, fallback: string): string => {
    if (err instanceof AxiosError) {
      const data = err.response?.data as
        | { error?: string; message?: string; mensagem?: string }
        | undefined
      // Prioriza a mensagem detalhada/acionável do backend (`mensagem`/`message`)
      // sobre o rótulo genérico (`error`, ex.: "Erro no processamento do upload").
      return data?.mensagem || data?.message || data?.error || err.message || fallback
    }
    return err instanceof Error ? err.message : fallback
  }

  // Mapeia o status do job (backend) para o status da máquina de estados da UI
  const mapearStatus = (s: JobStatusBackend): ImportacaoStatus => {
    switch (s) {
      case 'RECEBIDO':
        return 'queued'
      case 'CARREGANDO':
      case 'PROCESSANDO':
        return 'processing'
      case 'CONCLUIDO':
        return 'completed'
      case 'ERRO':
        return 'error'
      default:
        return 'processing'
    }
  }

  // Extrai campos de identificação dos dados crus para os "erros de inserção"
  const extrairIdentificacao = (dados: Record<string, unknown>): Partial<ErroInsercao> => {
    const str = (v: unknown) => (v === undefined || v === null || v === '' ? undefined : String(v))
    return {
      cliente: str(dados.cliente),
      fornecedor: str(dados.fornecedor),
      credor: str(dados.credor),
      ativo: str(dados.ativo_descricao),
      ativo_codigo: str(dados.ativo_codigo),
      ativo_placa: str(dados.ativo_placa),
      cpf_cnpj: str(dados.cpf_cnpj)
    }
  }

  // Monta o ProcessamentoResult (consumido pelo ImportSummary) a partir dos
  // totais do job e dos itens que falharam.
  const montarResultado = (job: JobStatusResponse, itens: ItemFalha[]): ProcessamentoResult => {
    const t = job.totais
    return {
      total: t.registros,
      inseridos: t.inseridos,
      atualizados: 0,
      erros: t.invalidos + t.erros,
      duplicados: t.duplicados,
      detalhesErros: [],
      tempoProcessamento: 0,
      info: {
        nomeArquivo: job.nomeArquivo,
        totalRegistros: t.registros,
        registrosValidos: t.inseridos,
        registrosInseridos: t.inseridos,
        registrosInvalidos: t.invalidos,
        registrosDuplicados: t.duplicados,
        errosInsercao: t.erros
      },
      registrosInvalidos: itens
        .filter(i => i.status === 'INVALIDO')
        .map(i => ({ linha: i.linha, dadosOriginais: i.dados, erros: i.erros })),
      registrosDuplicados: itens
        .filter(i => i.status === 'DUPLICADO')
        .map(i => ({ linha: i.linha, dadosOriginais: i.dados, motivo: i.motivo || 'Registro duplicado' })),
      errosInsercao: itens
        .filter(i => i.status === 'ERRO')
        .map(i => ({
          erro: i.motivo || i.erros[0]?.mensagem || 'Erro ao inserir',
          linha: i.linha,
          ...extrairIdentificacao(i.dados)
        }))
    }
  }

  // Busca TODAS as páginas de itens com falha. O backend pode devolver, por página,
  // menos itens do que o `limit` pedido (o `limit` é só um teto) — então paginamos
  // até acumular `total` itens, em vez de confiar numa única requisição.
  const buscarTodosItensFalha = async (jobId: string): Promise<ItemFalha[]> => {
    const itens: ItemFalha[] = []
    let pagina = 1
    const MAX_PAGINAS = 200 // trava de segurança contra loop infinito em caso de resposta inconsistente da API
    for (let i = 0; i < MAX_PAGINAS; i++) {
      const resp = await importacaoService.getJobItensFalha(jobId, STATUS_FALHA, pagina, LIMITE_ITENS_FALHA)
      if (resp.data.length === 0) break
      itens.push(...resp.data)
      if (itens.length >= resp.total) break
      pagina += 1
    }
    return itens
  }

  // Busca os itens que falharam e finaliza o fluxo (completed/error)
  const finalizar = async (job: JobStatusResponse) => {
    let itens: ItemFalha[] = []
    try {
      itens = await buscarTodosItensFalha(job.jobId)
    } catch {
      // Sem os detalhes, segue apenas com os totais
    }
    store.setItensFalha(itens)
    store.setProcessamento(montarResultado(job, itens))

    if (job.status === 'ERRO') {
      store.setErro(job.erro || 'A importação falhou.')
      store.setStatus('error')
    } else {
      store.setStatus('completed')
    }
  }

  // Uma checagem de status (chamada pelo polling)
  const verificarStatus = async (id: string) => {
    if (consultando) return
    consultando = true
    try {
      const job = await importacaoService.getJobStatus(id)
      store.setProcessados(job.progress?.processed ?? 0)
      store.setTotalParaProcessar(job.progress?.total || store.totalParaProcessar)

      if (job.status === 'CONCLUIDO' || job.status === 'ERRO') {
        pararPolling()
        await finalizar(job)
      } else {
        store.setStatus(mapearStatus(job.status))
      }
    } catch (err) {
      pararPolling()
      store.setErro(extrairMensagemErro(err, 'Erro ao consultar o status da importação.'))
      store.setStatus('error')
    } finally {
      consultando = false
    }
  }

  const iniciarPolling = (id: string) => {
    pararPolling()
    pollHandle = setInterval(() => {
      void verificarStatus(id)
    }, POLL_INTERVAL)
    // Primeira checagem imediata (não espera o primeiro intervalo)
    void verificarStatus(id)
  }

  // Selecionar tipo de cadastro
  const selecionarTipo = (tipo: TipoCadastro) => {
    store.setTipoCadastro(tipo)
  }

  // Seleção de arquivo (sem validação prévia)
  // O arquivo é enviado direto para a fila; o worker valida cada registro durante
  // o processamento e os que falharem aparecem no grid de correção para o usuário
  // ajustar e reprocessar. Não lê o arquivo aqui (alivia RAM do navegador e do servidor).
  const uploadArquivo = (file: File) => {
    if (!store.tipoCadastro) {
      store.setErro('Selecione o tipo de cadastro antes de fazer o upload.')
      return
    }

    store.setArquivo(file)
    store.limparErro()
    store.setStatus('validated')
  }

  // Remover arquivo
  const removerArquivo = () => {
    pararPolling()
    store.limparArquivo()
  }

  // Enfileira a importação e inicia o acompanhamento (polling)
  const processarImportacao = async () => {
    if (!store.tipoCadastro || !store.upload.arquivo) {
      store.setErro('Arquivo não selecionado.')
      return
    }

    store.limparErro()
    store.setTotalParaProcessar(0) // o total real é informado pelo job durante o processamento
    store.setProcessados(0)
    store.setStatus('queued')

    try {
      const { jobId: novoJobId } = await importacaoService.processarImportacao(
        store.tipoCadastro,
        store.upload.arquivo
      )
      store.setJobId(novoJobId)
      iniciarPolling(novoJobId)
    } catch (err) {
      store.setErro(extrairMensagemErro(err, 'Erro ao enviar a importação.'))
      store.setStatus('error')
    }
  }

  // Salva as correções dos itens e re-enfileira o job (reprocessa os PENDENTE)
  const corrigirEReprocessar = async (itensCorrigidos: ItemCorrecao[]) => {
    if (!store.jobId) {
      store.setErro('Não há um job ativo para reprocessar.')
      return
    }

    store.limparErro()
    store.setProcessados(0)
    store.setStatus('queued')

    try {
      if (itensCorrigidos.length > 0) {
        await importacaoService.salvarCorrecoes(store.jobId, itensCorrigidos)
      }
      await importacaoService.reprocessarJob(store.jobId)
      iniciarPolling(store.jobId)
    } catch (err) {
      store.setErro(extrairMensagemErro(err, 'Erro ao reprocessar a importação.'))
      store.setStatus('error')
    }
  }

  // Retoma o acompanhamento do job atual do usuário ao (re)carregar a tela.
  // Em andamento → religa o polling; concluído com pendências → reabre o grid.
  const retomarJobAtual = async () => {
    let job: JobStatusResponse | null = null
    try {
      job = await importacaoService.getJobAtual()
    } catch {
      return
    }
    if (!job) return

    const ativo =
      job.status === 'RECEBIDO' || job.status === 'CARREGANDO' || job.status === 'PROCESSANDO'
    const concluidoComPendencias =
      job.status === 'CONCLUIDO' && job.totais.invalidos + job.totais.erros > 0

    if (!ativo && !concluidoComPendencias) return

    // setTipoCadastro limpa o estado — definir antes do restante
    store.setTipoCadastro(job.tipo)
    store.setJobId(job.jobId)
    store.setTotalParaProcessar(job.progress.total)
    store.setProcessados(job.progress.processed)

    if (ativo) {
      store.setStatus(mapearStatus(job.status))
      iniciarPolling(job.jobId)
    } else {
      await finalizar(job)
    }
  }

  // Nova importação (arquiva o job atual para não reaparecer ao recarregar a tela)
  const novaImportacao = async () => {
    pararPolling()
    const id = store.jobId
    if (id) {
      try {
        await importacaoService.arquivarJob(id)
      } catch {
        // best-effort: se falhar, o job pode reaparecer no próximo carregamento
      }
    }
    store.resetar()
  }

  // Progresso em percentual
  const progressoPercentual = computed(() => {
    if (store.totalParaProcessar === 0) return 0
    return Math.round((store.processados / store.totalParaProcessar) * 100)
  })

  // Garante que o polling pare ao desmontar o componente
  onUnmounted(() => {
    pararPolling()
  })

  return {
    // Estados
    tipoCadastro,
    status,
    arquivo,
    validacaoLocal,
    validacaoApi,
    processamento,
    erro,
    processados,
    totalParaProcessar,
    progressoPercentual,
    jobId,
    itensFalha,

    // Ações
    selecionarTipo,
    uploadArquivo,
    removerArquivo,
    processarImportacao,
    corrigirEReprocessar,
    retomarJobAtual,
    novaImportacao
  }
}
